import {
  AfterViewInit,
  Component,
  ElementRef,
  OnChanges,
  OnDestroy,
  ViewChild,
  effect,
  inject,
  input,
} from '@angular/core';
import { ArcElement, Chart, DoughnutController, Legend, Tooltip } from 'chart.js';

import { ThemeService } from '../../../services/theme.service';

Chart.register(DoughnutController, ArcElement, Legend, Tooltip);

export interface HubSlice {
  label: string;
  value: number;
  /** A CSS colour, `var(--c-…)` or bare `--c-…` token (resolved at render time, theme-aware). */
  color: string;
}

function resolveColor(value: string, fallback = ''): string {
  const match = /^\s*(?:var\(\s*)?(--[\w-]+)/.exec(value);
  if (!match) {
    return value || fallback;
  }
  return getComputedStyle(document.documentElement).getPropertyValue(match[1]).trim() || fallback;
}

/** Doughnut chart (Chart.js) for share-of-total breakdowns. */
@Component({
  selector: 'app-hub-doughnut-chart',
  standalone: true,
  template: `<canvas #canvas class="w-full!" role="img" [attr.aria-label]="ariaLabel()"></canvas>`,
  host: { class: 'relative block h-[260px]' },
})
export class HubDoughnutChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  readonly slices = input<HubSlice[]>([]);
  readonly ariaLabel = input('Chart');

  @ViewChild('canvas') private canvas?: ElementRef<HTMLCanvasElement>;
  private chart: Chart<'doughnut'> | null = null;
  private readonly theme = inject(ThemeService);
  private viewReady = false;

  constructor() {
    effect(() => {
      this.theme.resolved();
      if (this.viewReady) {
        requestAnimationFrame(() => this.render());
      }
    });
  }

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.render();
  }

  ngOnChanges(): void {
    this.render();
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  private render(): void {
    if (!this.canvas) {
      return;
    }
    this.chart?.destroy();
    const slices = this.slices();
    this.chart = new Chart(this.canvas.nativeElement, {
      type: 'doughnut',
      data: {
        labels: slices.map((slice) => slice.label),
        datasets: [
          {
            data: slices.map((slice) => slice.value),
            backgroundColor: slices.map((slice) => resolveColor(slice.color, 'gray')),
            borderColor: resolveColor('--c-card', 'white'),
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '62%',
        plugins: { legend: { position: 'bottom', labels: { color: resolveColor('--c-fg', 'black') } } },
      },
    });
  }
}
